/**
 * The dashboard's "Play" quick action: it calls `setPendingAutoJoin('3+2')`
 * right before navigating here, and whoever serves /playChess has to consume
 * that intent and queue the game (GameSearch/index.tsx L76-77, L284-300 on the
 * old lane).
 *
 * Mirrored rules:
 *  - consumed ONCE, on mount, so a plain visit (sidebar Play tab) never sees a
 *    stale intent (`consumePendingAutoJoin`, GameSearch L76);
 *  - the seek waits for the socket — GameSearch gates on its `socketReady`
 *    prop because `joinGame` without a client pops a blocking alert
 *    (index.js L3122-3128). The V2 lane has that bit in the store as
 *    `session.connected`, and also waits for `session.me`, which is the
 *    `username` the join packet carries;
 *  - `autoJoined` guards a second fire (GameSearch L284-287).
 *
 * The store is watched with `subscribe`, not with a selector: this hook runs in
 * the screen ROOT, and a root re-render per connectivity change is exactly what
 * the V2 lane avoids (UX spec §6a).
 */
import {useEffect, useRef, useState} from 'react';

import {liveGameStore} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {consumePendingAutoJoin} from '@/Utils/pendingAutoJoin';

import {controlToMs} from '../pages/home/models/timeControls';

export function useAutoJoin(): void {
  const [pending] = useState(() => consumePendingAutoJoin());
  const autoJoined = useRef(false);

  useEffect(() => {
    if (!pending) {
      return;
    }
    const {tcMs, incMs} = controlToMs(pending.control);

    const tryJoin = (state: LiveGameState) => {
      if (autoJoined.current) {
        return;
      }
      if (!state.session.connected || !state.session.me) {
        return;
      }
      // A game we are already in (same-tab re-entry, a reload snapshot) owns the
      // screen: never seek over it.
      if (state.game.phase !== 'none' && state.game.phase !== 'ended') {
        return;
      }
      autoJoined.current = true;
      liveGameStore.getState().actions.seek(tcMs, incMs, 'Play');
    };

    tryJoin(liveGameStore.getState());
    return liveGameStore.subscribe(tryJoin);
  }, [pending]);
}

export default useAutoJoin;
